import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChartBindingValue,
  CustomChartBreakdownItem,
  CustomChartValueType,
  IndicatorSelectHandler,
} from '@/entities/dashboards';
import { cn, preventShiftSelection, type QualityZoneRanges } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { StatBox } from '../../parts/StatBox';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS } from '../../utils';
import { chartElementBgClass, groupChartItems, type ChartAppearance } from '../chartElements';
import { chartDatePrecision, shownChartValue } from '../chartValues';
import { ChartCardShell } from '../ChartCardShell';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';

type Props = {
  title: string;
  items: CustomChartBreakdownItem[];
  /** Тип величины категории: по нему величина-дата показывается датой, а число — числом */
  valueType: CustomChartValueType;
  /** Пороги зон качества пользователя: ими окрашен блок со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: правила раскраски блоков по величине */
  appearance?: ChartAppearance;
  /** Переход к диалогам категории; не задан — блоки нажатий не принимают и нажимаемыми не показаны */
  onSelect?: IndicatorSelectHandler;
  /** Выбор категории наполняет связанную диаграмму; задан — выбор заменяет переход к диалогам */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранная категория — она подсвечена, повторный выбор снимает её */
  selectedElement?: ChartBindingValue;
};

/**
 * Показатель с переходом к выборке: категории показаны отдельными блоками с названием и величиной —
 * как «Зоны качества» и «Красные зоны» стандартного дашборда.
 *
 * Полос сравнения величин у этого типа нет: сравнение — дело столбчатой диаграммы, а этот тип
 * выбирают ради перехода к диалогам, и блок читается как то, что можно нажать. Блок, за которым
 * диалогов нет, разбирать нечего — он нажимаемым не показан.
 *
 * У диаграммы, выбор в которой наполняет связанную, выбор занят разбором: категория тогда не
 * уводит к списку диалогов, а выбирается на самом дашборде.
 *
 * Категории, отнесённые выборкой к группам, собраны в озаглавленные группы в порядке их первого
 * появления: одноимённые категории разных групп остаются разными.
 *
 * Число категорий заранее не известно, а доля ширины у диаграммы любая, поэтому блоки встают в
 * колонки по ширине самой диаграммы, а не по ширине экрана.
 */
export const SelectionBreakdownChartView: FC<Props> = ({
  title,
  items,
  valueType,
  zoneRanges,
  appearance,
  onSelect,
  onSelectElement,
  selectedElement,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);

  // Точность времени берётся по величинам всех категорий сразу — соседние блоки написаны одинаково.
  const precision = chartDatePrecision(
    items.map((item) => item.value),
    valueType,
  );

  const groups = groupChartItems(items);

  return (
    <ChartCardShell title={title}>
      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="@container flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.title} className="flex flex-col gap-2">
              {group.title && <h4 className="text-sm font-medium text-muted-foreground">{group.title}</h4>}
              <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3">
                {group.items.map((item, index) => {
                  const isSelectable = !!onSelectElement;
                  const isClickable = isSelectable || (!!onSelect && item.conversationIds.length > 0);
                  const isDimmed = selectedElement !== undefined && selectedElement !== item.label;
                  // Величина категории бывает и строкой — правила раскраски задают границы числа,
                  // и такая категория остаётся неокрашенной.
                  const colorClass = chartElementBgClass(
                    { value: typeof item.value === 'number' ? item.value : null },
                    appearance,
                    zoneRanges,
                  );
                  const block = (
                    <StatBox
                      label={item.label}
                      value={shownChartValue(item.value, valueType, precision)}
                      className={cn(
                        'h-full',
                        colorClass ?? 'bg-card',
                        isClickable && DASHBOARD_CLICKABLE_BLOCK_CLASS,
                        isDimmed && 'opacity-60',
                      )}
                    />
                  );

                  // Ключ по месту в перечне: подписи категорий в пределах группы могут совпасть.
                  if (!isClickable) return <div key={index}>{block}</div>;

                  return (
                    <div
                      key={index}
                      role="button"
                      tabIndex={-1}
                      aria-label={isSelectable ? t('presets.chart.selectElement') : t('presets.chart.openDialogs')}
                      onMouseDown={preventShiftSelection}
                      onClick={(event) =>
                        isSelectable ? onSelectElement(item.label) : onSelect?.(item, event.shiftKey)
                      }
                    >
                      {block}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <ChartLegend items={legendItems} />
        </div>
      )}
    </ChartCardShell>
  );
};
